import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as CircleModes from 'mapbox-gl-draw-circle';
import React from 'react';
import MapGL, {
    FullscreenControl,
    MapRef,
    Marker,
    NavigationControl,
    useControl
} from 'react-map-gl';
import { Combobox } from '@components/combobox';
import { useError } from '@components/hooks/useError';
import { Button } from '@components/ui/button';
import { MAPBOX_ACCESS_TOKEN } from '@core/map';
import {
    CircleGeometry,
    CommunityLocationDto,
    LocationGeometryType,
    PolygonGeometry
} from '@api/ChallengeClient';
import {
    apiToGeoJson,
    circleFeatureToApi,
    circleGeometryToFeature,
    polygonFeatureToApi
} from './geometry';

export type DrawnGeometry =
    | {
          geometryType: LocationGeometryType.POLYGON;
          polygon: PolygonGeometry;
          circle?: undefined;
      }
    | {
          geometryType: LocationGeometryType.CIRCLE;
          circle: CircleGeometry;
          polygon?: undefined;
      };

export interface LocationMapHandle {
    setCircleRadius(meters: number): void;
}

interface LocationMapProps {
    geometryType: LocationGeometryType;
    initial?: CommunityLocationDto;
    onGeometryChange: (geometry: DrawnGeometry | null) => void;
}

// Whole of Poland fits at this center/zoom; regional (voivodeship) zoom is used
// once the browser shares the user's location.
const POLAND_VIEW = { longitude: 19.1451, latitude: 52.0693, zoom: 5.3 };
const REGION_ZOOM = 8.5;

const initialView = (initial?: CommunityLocationDto) => {
    if (initial?.circle) {
        return {
            longitude: initial.circle.center.longitude,
            latitude: initial.circle.center.latitude,
            zoom: 14
        };
    }
    const first = initial?.polygon?.points[0];
    if (first) {
        return {
            longitude: first.longitude,
            latitude: first.latitude,
            zoom: 14
        };
    }
    return POLAND_VIEW;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const featureToGeometry = (feature: any): DrawnGeometry =>
    feature.properties?.isCircle
        ? {
              geometryType: LocationGeometryType.CIRCLE,
              circle: circleFeatureToApi(feature)
          }
        : {
              geometryType: LocationGeometryType.POLYGON,
              polygon: polygonFeatureToApi(feature)
          };

interface PlaceSuggestion {
    id: string;
    label: string;
    longitude: number;
    latitude: number;
}

const PlaceSearch = ({
    onPick
}: {
    onPick: (longitude: number, latitude: number) => void;
}) => {
    const showError = useError();
    const [options, setOptions] = React.useState<PlaceSuggestion[]>([]);
    const [input, setInput] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [picked, setPicked] = React.useState<PlaceSuggestion | null>(null);

    React.useEffect(() => {
        const q = input.trim();
        if (q.length < 2) {
            setOptions([]);
            return;
        }
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const url = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(
                    q
                )}&limit=5&access_token=${MAPBOX_ACCESS_TOKEN}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Place search failed (${response.status})`);
                }
                const data = await response.json();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const suggestions: PlaceSuggestion[] = (
                    data?.features ?? []
                ).map(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (f: any) => ({
                        id: `${f.id ?? f.geometry.coordinates.join(',')}`,
                        label:
                            f.properties?.full_address ??
                            f.properties?.name ??
                            'Unknown place',
                        longitude: f.geometry.coordinates[0],
                        latitude: f.geometry.coordinates[1]
                    })
                );
                setOptions(suggestions);
            } catch (e) {
                setOptions([]);
                showError(e);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [input]);

    return (
        <div className="absolute top-2 left-1/2 z-10 w-[360px] max-w-[75%] -translate-x-1/2">
            <Combobox<PlaceSuggestion>
                size="small"
                className="[&_button]:bg-background"
                options={options}
                loading={loading}
                value={picked}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.id}
                placeholder="Search a place (e.g. DHL Kraków)"
                searchPlaceholder="Search a place (e.g. DHL Kraków)"
                emptyText="Type to search…"
                onInputChange={setInput}
                onChange={(value) => {
                    setPicked(value);
                    if (value) {
                        onPick(value.longitude, value.latitude);
                    }
                }}
            />
        </div>
    );
};

interface DrawLayerProps {
    geometryType: LocationGeometryType;
    initial?: CommunityLocationDto;
    drawRef: React.MutableRefObject<MapboxDraw | null>;
    onChanged: (geometry: DrawnGeometry) => void;
    onCleared: () => void;
}

const DrawLayer = ({
    geometryType,
    initial,
    drawRef,
    onChanged,
    onCleared
}: DrawLayerProps) => {
    const cbRef = React.useRef({ onChanged, onCleared });
    cbRef.current = { onChanged, onCleared };
    const initialisedRef = React.useRef(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDraw = (e: any) => {
        const draw = drawRef.current;
        const feature = e.features?.[0];
        if (!draw || !feature) {
            return;
        }
        // enforce exactly one geometry: drop anything that isn't the newest
        draw.getAll().features.forEach((f) => {
            if (f.id !== feature.id && f.id != null) {
                draw.delete(`${f.id}`);
            }
        });
        cbRef.current.onChanged(featureToGeometry(feature));
    };

    const onDelete = () => cbRef.current.onCleared();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useControl<any>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ map }: any) => {
            const draw = new MapboxDraw({
                displayControlsDefault: false,
                userProperties: true,
                controls: { trash: true },
                modes: {
                    ...MapboxDraw.modes,
                    draw_circle: CircleModes.DragCircleMode,
                    direct_select: CircleModes.DirectMode,
                    simple_select: CircleModes.SimpleSelectMode
                }
            });
            drawRef.current = draw;
            map.on('draw.create', onDraw);
            map.on('draw.update', onDraw);
            map.on('draw.delete', onDelete);
            return draw;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ map }: any) => {
            map.off('draw.create', onDraw);
            map.off('draw.update', onDraw);
            map.off('draw.delete', onDelete);
            drawRef.current = null;
        },
        { position: 'top-left' }
    );

    // preload existing geometry once (map stays pannable — no auto-draw)
    React.useEffect(() => {
        const draw = drawRef.current;
        if (!draw || initialisedRef.current || !initial) {
            return;
        }
        initialisedRef.current = true;
        const feature = apiToGeoJson(initial);
        draw.add({ type: 'FeatureCollection', features: [feature] });
        cbRef.current.onChanged(featureToGeometry(feature));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // switching the type clears any in-progress geometry (does not auto-arm)
    const firstTypeRun = React.useRef(true);
    React.useEffect(() => {
        if (firstTypeRun.current) {
            firstTypeRun.current = false;
            return;
        }
        drawRef.current?.deleteAll();
        cbRef.current.onCleared();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geometryType]);

    return null;
};

export const LocationMap = React.forwardRef<
    LocationMapHandle,
    LocationMapProps
>(({ geometryType, initial, onGeometryChange }, ref) => {
    const drawRef = React.useRef<MapboxDraw | null>(null);
    const mapRef = React.useRef<MapRef>(null);

    React.useImperativeHandle(ref, () => ({
        setCircleRadius: (meters: number) => {
            const draw = drawRef.current;
            if (!draw || meters <= 0) {
                return;
            }
            const existing = draw
                .getAll()
                .features.find((f) => f.properties?.isCircle);
            if (
                existing &&
                Math.round((existing.properties?.radiusInKm ?? 0) * 1000) ===
                    Math.round(meters)
            ) {
                return; // unchanged — keep the current feature and its selection
            }
            const existingCenter = existing?.properties?.center as
                | [number, number]
                | undefined;
            const mapCenter = mapRef.current?.getCenter();
            // fall back to the current map centre so a typed radius can create a
            // circle when none has been drawn yet
            const center =
                existingCenter ??
                (mapCenter ? [mapCenter.lng, mapCenter.lat] : undefined);
            if (!center) {
                return;
            }
            const feature = circleGeometryToFeature({
                center: { longitude: center[0], latitude: center[1] },
                radiusMeters: meters
            });
            draw.deleteAll();
            draw.add({ type: 'FeatureCollection', features: [feature] });
            setHasGeometry(true);
            onGeometryChange(featureToGeometry(feature));
        }
    }));

    const [hasGeometry, setHasGeometry] = React.useState(Boolean(initial));
    const [picked, setPicked] = React.useState<{
        longitude: number;
        latitude: number;
    } | null>(null);
    const view = initialView(initial);

    const goToPlace = (longitude: number, latitude: number) => {
        setPicked({ longitude, latitude });
        mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 15 });
    };

    // For new restrictions, zoom to the user's region if the browser allows it.
    React.useEffect(() => {
        if (initial || !navigator.geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) =>
                mapRef.current?.flyTo({
                    center: [
                        position.coords.longitude,
                        position.coords.latitude
                    ],
                    zoom: REGION_ZOOM
                }),
            () => undefined,
            { timeout: 5000 }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const arm = () => {
        const draw = drawRef.current;
        if (!draw) {
            return;
        }
        draw.deleteAll();
        setHasGeometry(false);
        onGeometryChange(null);
        const mode =
            geometryType === LocationGeometryType.CIRCLE
                ? 'draw_circle'
                : 'draw_polygon';
        // 'draw_circle' is a custom mode unknown to the MapboxDraw types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        draw.changeMode(mode as any);
    };

    return (
        <div className="relative h-full min-h-[400px] w-full">
            <MapGL
                ref={mapRef}
                initialViewState={{
                    longitude: view.longitude,
                    latitude: view.latitude,
                    zoom: view.zoom
                }}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
            >
                <FullscreenControl position="top-left" />
                <NavigationControl position="top-left" />
                <DrawLayer
                    geometryType={geometryType}
                    initial={initial}
                    drawRef={drawRef}
                    onChanged={(geometry) => {
                        setHasGeometry(true);
                        onGeometryChange(geometry);
                    }}
                    onCleared={() => {
                        setHasGeometry(false);
                        onGeometryChange(null);
                    }}
                />
                {picked && (
                    <Marker
                        longitude={picked.longitude}
                        latitude={picked.latitude}
                        color="#d32f2f"
                    />
                )}
            </MapGL>

            <PlaceSearch onPick={goToPlace} />

            {!hasGeometry && (
                <Button
                    size="sm"
                    onClick={arm}
                    className="absolute top-2 right-2 z-10"
                >
                    Draw{' '}
                    {geometryType === LocationGeometryType.CIRCLE
                        ? 'circle'
                        : 'polygon'}
                </Button>
            )}
        </div>
    );
});

LocationMap.displayName = 'LocationMap';
